import { memo, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ThinkingAccordion,
  ThinkingPassageAndChiclets,
  ThinkingWorkingPanel,
  MemoryPreservedLink,
  buildThinkingStepTaskCountTitle,
  countSubstantiveThinkingTasks,
  getVisibleThinkingPanelItems,
  thinkingChicletsWouldRender,
  thinkingStepChromeIcons,
  type MentionedEntity,
  type ThinkingStepData,
  type WritePovTenseChiclet,
  type WriteSettingsChicletValues,
} from "@sudowrite/shared-chat";
import { openChatPreferences } from "src/lib/openChatPreferences";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import type { CTAAction } from "./cta-types";
import { CTAInputBody } from "./CTAInputBody";
import { CTACollapsedInstructions } from "./CTACollapsedInstructions";
import { CTAEntityGenerateDetails } from "./CTAEntityGenerateDetails";
import { SPRING_BOUNCY, FADE_NORMAL } from "../Plan/animations";

const onWriteSettingsPress = () =>
  useSudowriteStore.getState().openStyleSettings("writing");

type Props = {
  data: ThinkingStepData;
  model?: string | null;
  writeSettings?: WriteSettingsChicletValues;
  writePovTenseChiclets?: WritePovTenseChiclet[];
  contextElements?: string[];
  mentionedEntities?: MentionedEntity[];
  highlightedPassage?: string;
  insertedPassage?: string;
  streaming?: boolean;
  collapseForSuggestion?: boolean;
  icon?: React.ReactNode;
  ctaAction?: CTAAction;
  onCtaGo?: (customInstructions: string, extra?: { role?: string; fields?: Array<{ id?: string; label: string; value?: unknown }> }) => void;
  onCtaCancel?: () => void;
};

const ACTION_ICONS = ["list", "characters", "worldbuilding", "meta"] as const;

const sharedThinkingPanelProps = {
  chicletIcons: thinkingStepChromeIcons,
  renderHeaderSparkle: (animated: boolean) =>
    animated
      ? thinkingStepChromeIcons.streamingSparkleAnimated
      : thinkingStepChromeIcons.streamingSparkle,
  onMemoryPreservedPress: () =>
    openChatPreferences({ advanced: true, source: "memory_preserved_thinking_link" }),
  onWriteSettingsPress,
};

/**
 * When false, hide model reasoning / ⟦status⟧ snippets in the thinking panel.
 * Tool progress rows still show. Temporary for the agentic model A/B branch.
 */
export const SHOW_REASONING_STATUS_LINES_IN_CHAT = false;

const READ_STATUS_PREFIX = /^(?:check(?:ing|ed)|read(?:ing)?)\s+/i;

function readTaskDedupeKey(task: ThinkingStepData["tasks"][number]): string | null {
  if (task.icon === "brain" || task.openChatPreferencesLink || task.status === "error" || task.isWrite) {
    return null;
  }

  // Incomplete streamed or persisted tasks must not be dropped as duplicate reads.
  if (typeof task.title !== "string" || !task.title.trim()) return null;
  const title = task.title.trim();
  const toolName = task.toolName?.toLowerCase() ?? "";
  const isReadTask =
    READ_STATUS_PREFIX.test(title)
    || toolName === "read"
    || toolName === "read_file"
    || toolName === "list"
    || toolName.includes("read")
    || toolName.includes("view");

  if (!isReadTask) return null;

  const titleTarget = title
    .replace(READ_STATUS_PREFIX, "")
    .replace(/\.{1,3}$/, "")
    .trim()
    .toLowerCase();
  const target = task.elementType === "document"
    ? titleTarget
    : task.elementType ?? titleTarget;

  return target ? `read:${target}` : null;
}

function dedupeThinkingPanelReadTasks(
  tasks: ThinkingStepData["tasks"],
): ThinkingStepData["tasks"] {
  const seen = new Set<string>();

  return tasks.filter((task) => {
    const key = readTaskDedupeKey(task);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function tasksForThinkingPanel(tasks: ThinkingStepData["tasks"]): ThinkingStepData["tasks"] {
  const dedupedTasks = dedupeThinkingPanelReadTasks(tasks);
  return dedupedTasks.map((task) => ({
    ...task,
    title: task.title?.trim() ? task.title : task.status === "complete"
      ? "Completed task"
      : task.status === "error" ? "Task failed" : "Working...",
    ...(!SHOW_REASONING_STATUS_LINES_IN_CHAT && task.icon === "brain"
      ? { explanation: undefined }
      : {}),
  }));
}

const isEntityGenerateCta = (type: CTAAction["type"] | undefined) =>
  type === "generate_character" || type === "generate_worldbuilding";

const deriveActionTitle = (tasks: ThinkingStepData["tasks"]): string => {
  const entityTypes = new Set<string>();
  for (const task of tasks) {
    if (task.elementType) {
      entityTypes.add(task.elementType);
    } else if (task.icon === "list") {
      entityTypes.add("outline");
    } else if (task.icon === "characters") {
      entityTypes.add("character");
    } else if (task.icon === "worldbuilding") {
      entityTypes.add("worldbuilding");
    } else if (task.icon === "meta") {
      entityTypes.add("meta");
    }
  }

  if (entityTypes.size === 0) return "Update Outline";
  if (entityTypes.size >= 3) return "Update Story Bible";

  const labels: Record<string, string> = {
    outline: "Outline",
    character: "Characters",
    worldbuilding: "Worldbuilding",
    meta: "Story Bible",
  };
  const names = Array.from(entityTypes).map((t) => labels[t]);
  return names.length === 1 ? `Update ${names[0]}` : `Update ${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
};

function deriveThinkingAccordionTitle(data: ThinkingStepData, streaming: boolean): string {
  if (streaming) return "Working...";
  return buildThinkingStepTaskCountTitle(data.tasks, false);
}

function buildPassageChicletProps(
  props: Props,
  showChiclets: boolean,
) {
  const hasChiclets = thinkingChicletsWouldRender({
    contextElements: props.contextElements,
    mentionedEntities: props.mentionedEntities,
    writeSettings: props.writeSettings,
    writePovTenseChiclets: props.writePovTenseChiclets,
  });

  return {
    highlightedPassage: props.highlightedPassage,
    insertedPassage: props.insertedPassage,
    hasChiclets,
    showChiclets,
    contextElements: props.contextElements,
    mentionedEntities: props.mentionedEntities,
    writeSettings: props.writeSettings,
    writePovTenseChiclets: props.writePovTenseChiclets,
    chicletIcons: thinkingStepChromeIcons,
    expandableTextIcons: {
      chevronRight: thinkingStepChromeIcons.chevronRight,
      chevronDown: thinkingStepChromeIcons.chevronDownSmall,
    },
    onWriteSettingsPress,
  };
}

export const ThinkingStep: React.FC<Props> = memo((props) => {
  const {
    data,
    model,
    writeSettings,
    writePovTenseChiclets,
    contextElements,
    mentionedEntities,
    highlightedPassage,
    insertedPassage,
    streaming,
    collapseForSuggestion,
    icon,
    ctaAction,
    onCtaGo,
    onCtaCancel,
  } = props;

  const [accordionValue, setAccordionValue] = useState<string>("");
  const prevStreamingRef = useRef(streaming);

  const isCTA = !!ctaAction;
  const ctaStatus = ctaAction?.status;

  const hasActionTask = data.tasks.some(
    (task) => ACTION_ICONS.includes(task.icon as (typeof ACTION_ICONS)[number]) && task.isWrite,
  );
  const showWriteMessageChiclets = false;
  const passageProps = buildPassageChicletProps(props, showWriteMessageChiclets);
  const hasChiclets = passageProps.hasChiclets;
  const hasTasks = data.tasks.length > 0;
  const substantiveTaskCount = countSubstantiveThinkingTasks(data.tasks);
  const hasVisibleCompletedTaskRows = hasTasks
    ? getVisibleThinkingPanelItems(data.tasks, { streaming: false }).length > 0
    : false;
  const hasVisibleTaskRows = streaming ? hasTasks : hasVisibleCompletedTaskRows;
  const useThinkingWorkAccordion = hasTasks && !isCTA && (streaming || substantiveTaskCount > 0);
  const hasContent = hasVisibleTaskRows
    || (hasChiclets && (useThinkingWorkAccordion || hasActionTask || isCTA || !!streaming))
    || !!highlightedPassage
    || !!insertedPassage
    || isCTA;

  const hasMemoryPreservedLink = data.tasks.some(
    (t) => "openChatPreferencesLink" in t && t.openChatPreferencesLink && t.status === "complete",
  );

  useEffect(() => {
    if (useThinkingWorkAccordion) {
      if (collapseForSuggestion) {
        setAccordionValue("");
      } else if (streaming) {
        setAccordionValue("thinking");
      } else if (prevStreamingRef.current) {
        setAccordionValue("");
      }
    } else if (prevStreamingRef.current && !streaming && hasActionTask && !hasMemoryPreservedLink && !isCTA) {
      setAccordionValue("");
    }
    prevStreamingRef.current = streaming;
  }, [streaming, collapseForSuggestion, useThinkingWorkAccordion, hasActionTask, hasMemoryPreservedLink, isCTA]);

  if (!hasContent) {
    return null;
  }

  const panelTasks = tasksForThinkingPanel(data.tasks);

  const panel = (
    <div className="px-3">
      <ThinkingWorkingPanel
        {...sharedThinkingPanelProps}
        tasks={panelTasks}
        streaming={!!streaming}
        embedded
        showHeader={false}
        excludeMemoryLink={false}
        model={model}
        writeSettings={writeSettings}
        writePovTenseChiclets={writePovTenseChiclets}
        mentionedEntities={mentionedEntities}
      />
    </div>
  );

  if (isCTA) {
    const title = ctaAction.displayTitle;
    const isCancelled = ctaStatus === "cancelled";
    const isAwaitingInput = ctaStatus === "awaiting_input";
    const isInProgress = ctaStatus === "in_progress";
    const isCompleted = ctaStatus === "completed";
    const showToolProgress = (isInProgress || isCompleted) && hasTasks;
    const keepCtaAccordionOpen = isAwaitingInput || (isInProgress && streaming);
    const isCollapsible = !keepCtaAccordionOpen && (isCompleted || (isInProgress && !streaming));
    const expanded = keepCtaAccordionOpen || accordionValue === "action";

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ scale: SPRING_BOUNCY, opacity: FADE_NORMAL }}
      >
        <ThinkingAccordion
          expanded={expanded}
          collapsible={isCollapsible}
          onExpandedChange={isCollapsible ? (next) => setAccordionValue(next ? "action" : "") : undefined}
          title={title}
          titleCancelled={isCancelled}
          leading={
            isCompleted
              ? thinkingStepChromeIcons.completedCheck
              : thinkingStepChromeIcons.defaultSparkle
          }
          trailingStatus={isCancelled ? "Cancelled" : undefined}
          showChevron={isCollapsible}
          icons={thinkingStepChromeIcons}
          // Match master: awaiting_input omitted overflow-hidden so role/type selects aren't clipped.
          clipOverflow={!isAwaitingInput}
          testId="thinking-cta-accordion"
        >
          <AnimatePresence>
            {isAwaitingInput && onCtaGo && onCtaCancel ? (
              <CTAInputBody
                key="cta-input"
                actionType={ctaAction.type}
                onGo={onCtaGo}
                onCancel={onCtaCancel}
                instantFired={ctaAction.instantFired}
              />
            ) : null}

            {(isInProgress || isCompleted) && (
              <motion.div
                key="cta-progress"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                {isEntityGenerateCta(ctaAction.type) ? (
                  <CTAEntityGenerateDetails ctaAction={ctaAction} />
                ) : ctaAction.customInstructions ? (
                  <CTACollapsedInstructions instructions={ctaAction.customInstructions} />
                ) : null}
                {showToolProgress ? (
                  <>
                    <ThinkingPassageAndChiclets {...passageProps} />
                    {panel}
                  </>
                ) : null}
              </motion.div>
            )}
          </AnimatePresence>
        </ThinkingAccordion>
      </motion.div>
    );
  }

  if (useThinkingWorkAccordion) {
    const thinkingTitle = deriveThinkingAccordionTitle(data, !!streaming);
    const isExpanded = (streaming && !collapseForSuggestion) || accordionValue === "thinking";
    const canCollapse = !streaming || Boolean(collapseForSuggestion);
    const memoryPreservedTask = data.tasks.find(
      (t) => "openChatPreferencesLink" in t && t.openChatPreferencesLink && t.status === "complete",
    );
    const showCollapsedMemoryLink = Boolean(memoryPreservedTask && canCollapse && !isExpanded);

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ scale: SPRING_BOUNCY, opacity: FADE_NORMAL }}
        className="w-full"
      >
        <ThinkingAccordion
          expanded={isExpanded}
          collapsible={canCollapse}
          onExpandedChange={
            canCollapse
              ? (next) => setAccordionValue(next ? "thinking" : "")
              : undefined
          }
          title={thinkingTitle}
          titleStreaming={!!streaming}
          leading={
            streaming
              ? thinkingStepChromeIcons.streamingSparkleAnimated
              : thinkingStepChromeIcons.completedCheck
          }
          icons={thinkingStepChromeIcons}
          testId="thinking-work-accordion"
          footer={
            showCollapsedMemoryLink && memoryPreservedTask ? (
              <MemoryPreservedLink
                task={memoryPreservedTask}
                onPress={() =>
                  openChatPreferences({ advanced: true, source: "memory_preserved_thinking_link" })
                }
              />
            ) : undefined
          }
        >
          <ThinkingPassageAndChiclets {...passageProps} />
          <div className="px-3">
            <ThinkingWorkingPanel
              {...sharedThinkingPanelProps}
              tasks={panelTasks}
              streaming={!!streaming}
              embedded
              showHeader={false}
              excludeMemoryLink={showCollapsedMemoryLink}
              model={model}
              writeSettings={writeSettings}
              writePovTenseChiclets={writePovTenseChiclets}
              mentionedEntities={mentionedEntities}
            />
          </div>
        </ThinkingAccordion>
      </motion.div>
    );
  }

  // Legacy: passages/chiclets only. Header chrome is action-task-only (parity with pre-shared Accordion).
  if (!hasActionTask) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ scale: SPRING_BOUNCY, opacity: FADE_NORMAL }}
        className="w-full rounded-xl bg-app-primary-cta-50 pt-3"
        data-testid="thinking-legacy-accordion"
      >
        <ThinkingPassageAndChiclets
          {...passageProps}
          showChiclets
        />
      </motion.div>
    );
  }

  const isCollapsible = !streaming || Boolean(collapseForSuggestion);
  const isExpanded = accordionValue === "action";
  const actionTitle = deriveActionTitle(data.tasks);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ scale: SPRING_BOUNCY, opacity: FADE_NORMAL }}
    >
      <ThinkingAccordion
        expanded={isExpanded || (!!streaming && !collapseForSuggestion)}
        collapsible={isCollapsible}
        onExpandedChange={
          isCollapsible
            ? (next) => setAccordionValue(next ? "action" : "")
            : undefined
        }
        title={actionTitle}
        leading={icon ?? thinkingStepChromeIcons.defaultSparkle}
        showChevron={!streaming}
        icons={thinkingStepChromeIcons}
        testId="thinking-legacy-accordion"
      >
        <ThinkingPassageAndChiclets
          {...passageProps}
          showChiclets
        />
      </ThinkingAccordion>
    </motion.div>
  );
});
